'use client'

import type { BigIntish, Fetchable } from '@morpho-org/blue-sdk'

import { type InterfaceToken, toBigInt, ZERO } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils'
import { Badge } from '@repo/ui'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

import { Avatar } from './Avatar'
import { HoverableCell } from './HoverableCell'

export interface CellTokenAmountPriceProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'content'
> {
  /** Token object with decimals, price, and logo */
  token: Fetchable<InterfaceToken>
  /** Token amount */
  amount?: BigIntish | null | undefined
  /** USD value (18 decimals or number) */
  value?: Fetchable<bigint> | number
  /** Net APY (18 decimals) */
  netApy?: Fetchable<bigint>
  /** Content to display in hover card */
  content?: ReactNode
  /** Show token avatar */
  withAvatar?: boolean
  /** Render prop for warning icon */
  renderWarning?: ReactNode
  /** Omit token symbol from formatted amount */
  omitSymbol?: boolean
  /** Show explicit +/- sign for positive/negative amounts (v5 parity) */
  sign?: boolean
}

/**
 * CellTokenAmountPrice component - Tailwind replica of dapp-ui-v5 CellTokenAmountPrice
 *
 * Displays token amount with optional avatar, value badge, and warning.
 * Supports hover card content for additional details.
 *
 * @example
 * ```tsx
 * <CellTokenAmountPrice
 *   token={token}
 *   amount={1000000000000000000n}
 *   value={1500000000000000000000n}
 *   withAvatar
 *   content={<HoverCardContent />}
 * />
 * ```
 */
const CellTokenAmountPrice = forwardRef<HTMLDivElement, CellTokenAmountPriceProps>(
  (
    {
      token,
      amount,
      value,
      netApy,
      content,
      withAvatar,
      renderWarning,
      omitSymbol,
      sign,
      className,
      ...props
    },
    ref
  ) => {
    const getFormatAmount = () => {
      if (!isDefined(amount) || !isDefined(token) || !isDefined(token.decimals)) {
        return '-'
      }
      const amountBigInt = toBigInt(amount)
      const absAmountBigInt = amountBigInt < ZERO ? -amountBigInt : amountBigInt

      const chain = formatters.commas
        .digits(getDigitsFromPriceAndAmount(token.price, absAmountBigInt, token.decimals))
        .default('-')
        .trailingZero(!amountBigInt)
        .unit(omitSymbol ? '' : token.symbol)

      return (sign ? chain.sign() : chain).of(amountBigInt, token.decimals)
    }

    const getFormatValue = () => {
      const chain = formatters.short.unit('$').digits(2)
      if (typeof value === 'number') {
        return (sign ? chain.sign() : chain).of(value)
      }
      return (sign ? chain.sign() : chain).of(value, 18)
    }

    const formatAmount = getFormatAmount()
    const formatValue = getFormatValue()

    return (
      <div ref={ref} className={cn('gap-2xs flex items-center', className)} {...props}>
        {/* Only pad when there's a hover card (V5 parity): the padding insets the
            hover highlight, so a cell with no hover content must stay flush with
            the column base — otherwise it indents the amount past the header. */}
        <HoverableCell hoverCardContent={content} withBackgroundEffect={!!content}>
          <div className="gap-2xs flex items-center">
            {withAvatar && token?.logoURI && <Avatar size="small" src={token.logoURI} />}

            <span className="text-2xs text-foreground flex h-[22px] items-center">
              {formatAmount}
            </span>
            {!!value && (
              <Badge variant="tertiary" size="sm">
                {formatValue}
              </Badge>
            )}
            {renderWarning}
          </div>
        </HoverableCell>
      </div>
    )
  }
)

CellTokenAmountPrice.displayName = 'CellTokenAmountPrice'

export { CellTokenAmountPrice }
